import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatRupiah } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, Truck, QrCode, MapPin, CheckCircle2, Search, Store } from "lucide-react";

const WEIGHTS = { mug: 600, plaque: 1000 };
const PICKUP = { courier: "Ambil Sendiri di Toko", courier_code: "pickup", service: "Ambil di Toko",
  description: "Ambil langsung di toko Rooftop Print", cost: 0, etd: "-" };

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    recipient_name: user?.name || "", phone: "", postal_code: "", address: "",
  });
  const [destQuery, setDestQuery] = useState("");
  const [destResults, setDestResults] = useState([]);
  const [destOpen, setDestOpen] = useState(false);
  const [destLoading, setDestLoading] = useState(false);
  const [destination, setDestination] = useState(null); // {id,label,city,province,postal_code}

  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [instantEnabled, setInstantEnabled] = useState(true);
  const [deliveryMode, setDeliveryMode] = useState("ship");
  const [sender, setSender] = useState(null);
  const placedRef = useRef(false);
  const debounceRef = useRef(null);
  const destBoxRef = useRef(null);

  const totalWeight = items.reduce((s, i) => s + (i.weight || WEIGHTS[i.product_type] || 500) * i.quantity, 0);

  useEffect(() => {
    if (items.length === 0 && !placedRef.current) navigate("/keranjang");
  }, [items, navigate]);

  useEffect(() => {
    api.get("/settings/shipping-config").then((r) => setInstantEnabled(r.data?.instant_enabled !== false)).catch(() => {});
    api.get("/settings/sender").then((r) => setSender(r.data)).catch(() => {});
  }, []);

  const selectPickup = () => { setDeliveryMode("pickup"); setSelectedShipping(PICKUP); setShippingOptions([]); };
  const selectShip = () => { setDeliveryMode("ship"); setSelectedShipping(null); };
  const isPickup = deliveryMode === "pickup";

  // Debounced destination search
  useEffect(() => {
    if (destination && destQuery === destination.label) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (destQuery.trim().length < 3) { setDestResults([]); setDestOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setDestLoading(true);
      try {
        const r = await api.get(`/shipping/destinations?search=${encodeURIComponent(destQuery.trim())}`);
        setDestResults(r.data || []);
        setDestOpen(true);
      } catch (e) {
        setDestResults([]);
        setDestOpen(true);
      } finally {
        setDestLoading(false);
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [destQuery]); // eslint-disable-line

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => { if (destBoxRef.current && !destBoxRef.current.contains(e.target)) setDestOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const resetShipping = () => { setShippingOptions([]); setSelectedShipping(null); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickDestination = (d) => {
    setDestination(d);
    setDestQuery(d.label);
    setDestOpen(false);
    setField("postal_code", d.postal_code || "");
    resetShipping();
  };

  const calcShipping = async () => {
    if (!destination) { toast.error("Pilih tujuan pengiriman terlebih dahulu"); return; }
    setCalcLoading(true);
    resetShipping();
    try {
      const res = await api.post("/shipping/cost", { destination_id: destination.id, weight: totalWeight });
      const opts = [...(res.data.results || [])];
      if (instantEnabled) {
        opts.push({ courier: "Instan", courier_code: "instant", service: "Gojek / Grab / Lalamove",
          description: "Biaya kirim dibayar oleh pembeli saat barang tiba", cost: 0, etd: "-" });
      }
      setShippingOptions(opts);
      if (!opts.length) toast.info("Tidak ada layanan tersedia untuk tujuan ini");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghitung ongkir");
    } finally {
      setCalcLoading(false);
    }
  };

  const shippingCost = selectedShipping?.cost || 0;
  const total = subtotal + shippingCost;

  const placeOrder = async () => {
    if (!form.recipient_name || !form.phone) {
      toast.error("Lengkapi nama & nomor HP penerima");
      return;
    }
    if (!isPickup) {
      if (!destination) { toast.error("Pilih tujuan pengiriman"); return; }
      if (!form.address || form.address.trim().length < 10) {
        toast.error("Alamat lengkap wajib diisi (nama jalan, no. rumah, RT/RW, kelurahan, kecamatan)");
        return;
      }
      if (!selectedShipping) { toast.error("Pilih layanan pengiriman"); return; }
    }
    if (!agreed) { toast.error("Silakan setujui Syarat & Ketentuan terlebih dahulu"); return; }

    setPlacing(true);
    try {
      const shipping_address = isPickup
        ? {
            recipient_name: form.recipient_name, phone: form.phone,
            destination_id: sender?.origin_id || 4854, destination_label: sender?.origin_label || "",
            province: "", city: "", postal_code: "",
            address: sender?.address || "Ambil di toko Rooftop Print, Jl. Pagarsih No.122, Cibadak, Astanaanyar, Bandung 40241",
          }
        : {
            recipient_name: form.recipient_name, phone: form.phone,
            destination_id: destination.id, destination_label: destination.label,
            province: destination.province || "", city: destination.city || "",
            postal_code: form.postal_code, address: form.address,
          };
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, quantity: i.quantity, design_file_id: i.design_file_id, notes: i.notes || "",
          variant_selections: i.variant_selections || [], design_files: i.design_files || [],
        })),
        shipping_address,
        shipping: isPickup ? PICKUP : selectedShipping,
        payment_method: "manual_qris",
      };
      const res = await api.post("/orders", payload);
      placedRef.current = true;
      clearCart();
      navigate(`/pembayaran/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat pesanan");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#00133F] mb-8">Checkout</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery mode */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="font-heading font-semibold text-lg text-[#00133F] mb-4">Metode Pengiriman</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" onClick={selectShip} data-testid="delivery-ship"
                  className={`text-left p-4 rounded-xl border transition-colors ${!isPickup ? "border-[#F0005A] bg-[#F0005A]/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="font-medium text-[#00133F] text-sm flex items-center gap-2"><Truck className="w-4 h-4" /> Kirim ke Alamat</p>
                  <p className="text-xs text-slate-500 mt-1">Ongkir dihitung otomatis via RajaOngkir.</p>
                </button>
                <button type="button" onClick={selectPickup} data-testid="delivery-pickup"
                  className={`text-left p-4 rounded-xl border transition-colors ${isPickup ? "border-[#F0005A] bg-[#F0005A]/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="font-medium text-[#00133F] text-sm flex items-center gap-2"><Store className="w-4 h-4" /> Ambil Sendiri di Toko</p>
                  <p className="text-xs text-[#10B981] mt-1 font-medium">Gratis (Rp0)</p>
                </button>
              </div>
            </section>

            {/* Shipping address */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-5">
                <MapPin className="w-5 h-5 text-[#F0005A]" />
                <h2 className="font-heading font-semibold text-lg text-[#00133F]">Data Pengiriman</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Nama Penerima</Label>
                  <Input data-testid="input-recipient" value={form.recipient_name} onChange={(e) => setField("recipient_name", e.target.value)} className="mt-1.5 rounded-xl focus-visible:ring-[#F0005A]/50" placeholder="Nama lengkap" />
                </div>
                <div>
                  <Label className="text-sm">No. HP / WhatsApp</Label>
                  <Input data-testid="input-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="mt-1.5 rounded-xl focus-visible:ring-[#F0005A]/50" placeholder="08xxxxxxxxxx" />
                </div>
                <div className="sm:col-span-2" ref={destBoxRef} hidden={isPickup}>
                  <Label className="text-sm">Tujuan (Kecamatan / Kota / Kode Pos)</Label>
                  <div className="relative mt-1.5">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="input-destination-search" value={destQuery}
                      onChange={(e) => { setDestQuery(e.target.value); setDestination(null); resetShipping(); }}
                      onFocus={() => destResults.length && setDestOpen(true)}
                      className="pl-9 rounded-xl focus-visible:ring-[#F0005A]/50"
                      placeholder="Ketik kecamatan / kota, mis. Kebayoran Lama" autoComplete="off" />
                    {destLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
                    {destOpen && destResults.length > 0 && (
                      <div data-testid="destination-results" className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                        {destResults.map((d) => (
                          <button key={d.id} type="button" data-testid={`destination-option-${d.id}`}
                            onClick={() => pickDestination(d)}
                            className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#F0005A]/5 border-b border-slate-50 last:border-0">
                            <span className="text-[#00133F]">{d.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {destOpen && destResults.length === 0 && !destLoading && destQuery.trim().length >= 3 && !destination && (
                      <p className="text-xs text-slate-400 mt-1.5" data-testid="destination-empty">Tujuan tidak ditemukan. Coba ketik nama kecamatan atau kota lain.</p>
                    )}
                  </div>
                  {destination && <p className="text-xs text-[#10B981] mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {destination.city}, {destination.province}</p>}
                </div>
                <div hidden={isPickup}>
                  <Label className="text-sm">Kode Pos</Label>
                  <Input data-testid="input-postal" value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} className="mt-1.5 rounded-xl focus-visible:ring-[#F0005A]/50" placeholder="Kode pos" />
                </div>
                <div className="sm:col-span-2" hidden={isPickup}>
                  <Label className="text-sm">Alamat Lengkap <span className="text-[#F0005A]">*wajib</span></Label>
                  <Input data-testid="input-address" value={form.address} onChange={(e) => setField("address", e.target.value)} className="mt-1.5 rounded-xl focus-visible:ring-[#F0005A]/50" placeholder="Nama Jalan, No. Rumah, RT/RW, Kelurahan, Kecamatan" />
                  <p className="text-xs text-slate-400 mt-1">Wajib diisi lengkap — jangan hanya kota. Contoh: Jl. Melati No. 10, RT01/RW02, Kel. Sukajadi, Kec. Sukajadi.</p>
                </div>
                {isPickup && (
                  <div className="sm:col-span-2 rounded-xl bg-[#F0005A]/5 border border-[#F0005A]/20 p-4" data-testid="pickup-info">
                    <p className="text-sm font-medium text-[#00133F] flex items-center gap-2"><Store className="w-4 h-4 text-[#F0005A]" /> Ambil di Toko Rooftop Print</p>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{sender?.address || "Jl. Pagarsih No.122, Cibadak, Kec. Astanaanyar, Kota Bandung, Jawa Barat 40241"}</p>
                    <p className="text-xs text-[#10B981] mt-2 font-medium">Ongkir Rp0 — pesanan diambil langsung di toko.</p>
                  </div>
                )}
              </div>
            </section>

            {/* Shipping method */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6" hidden={isPickup}>
              <div className="flex items-center gap-2 mb-5">
                <Truck className="w-5 h-5 text-[#F0005A]" />
                <h2 className="font-heading font-semibold text-lg text-[#00133F]">Pengiriman (RajaOngkir)</h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Button data-testid="calc-shipping-btn" onClick={calcShipping} disabled={calcLoading || !destination}
                  variant="outline" className="rounded-xl border-[#00133F] text-[#00133F]">
                  {calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cek Ongkir"}
                </Button>
                <span className="text-xs text-slate-400">Berat total: {(totalWeight / 1000).toFixed(1)} kg · semua kurir tersedia</span>
              </div>

              {shippingOptions.length > 0 && (
                <RadioGroup className="mt-5 space-y-3" value={selectedShipping ? JSON.stringify(selectedShipping) : ""}
                  onValueChange={(v) => setSelectedShipping(JSON.parse(v))}>
                  {shippingOptions.map((opt, idx) => {
                    const val = JSON.stringify(opt);
                    const active = selectedShipping && JSON.stringify(selectedShipping) === val;
                    return (
                      <label key={idx} data-testid={`shipping-option-${idx}`}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${active ? "border-[#F0005A] bg-[#F0005A]/5" : "border-slate-200 hover:border-slate-300"}`}>
                        <RadioGroupItem value={val} />
                        <div className="flex-1">
                          <p className="font-medium text-[#00133F] text-sm">{opt.courier} · {opt.service}</p>
                          <p className="text-xs text-slate-500">{opt.description}{opt.etd ? ` · estimasi ${opt.etd}` : ""}</p>
                        </div>
                        <span className="font-semibold text-[#00133F]">{opt.courier_code === "instant" ? "Bayar saat tiba" : formatRupiah(opt.cost)}</span>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </section>

            {/* Payment */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <QrCode className="w-5 h-5 text-[#F0005A]" />
                <h2 className="font-heading font-semibold text-lg text-[#00133F]">Metode Pembayaran</h2>
              </div>
              <div className="rounded-xl border border-[#F0005A]/20 bg-[#F0005A]/5 p-4" data-testid="payment-manual-info">
                <p className="font-medium text-[#00133F] text-sm">QRIS (Konfirmasi Manual via WhatsApp)</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                  Setelah membuat pesanan, kamu akan diarahkan ke halaman pembayaran berisi <strong>QRIS Rooftop Print</strong>.
                  Scan QRIS, lalu kirim bukti transfer ke WhatsApp kami untuk konfirmasi pesanan.
                </p>
              </div>
            </section>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-heading font-semibold text-lg text-[#00133F] mb-4">Ringkasan Pesanan</h3>
              <div className="space-y-3 max-h-52 overflow-auto mb-4">
                {items.map((i) => (
                  <div key={i.cart_id} className="flex gap-3">
                    <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-slate-50 shrink-0">
                      <img src={i.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 text-sm">
                      <p className="font-medium text-[#00133F] truncate">{i.product_name}</p>
                      <p className="text-slate-400 text-xs">{i.quantity} × {formatRupiah(i.unit_price)}</p>
                    </div>
                    <span className="text-sm font-medium">{formatRupiah(i.unit_price * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatRupiah(subtotal)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Ongkir</span><span>{selectedShipping ? formatRupiah(shippingCost) : "-"}</span></div>
                <div className="flex justify-between font-bold text-[#00133F] text-base pt-2 border-t border-slate-100"><span>Total</span><span data-testid="order-total">{formatRupiah(total)}</span></div>
              </div>
              <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="terms-checkbox" className="mt-0.5 data-[state=checked]:bg-[#F0005A] data-[state=checked]:border-[#F0005A]" />
                <span className="text-sm text-slate-600 leading-snug">
                  Saya telah menyetujui{" "}
                  <Link to="/syarat" target="_blank" className="text-[#F0005A] font-medium hover:underline" data-testid="terms-link">Syarat &amp; Ketentuan</Link>{" "}
                  yang berlaku
                </span>
              </label>
              <Button data-testid="place-order-btn" onClick={placeOrder} disabled={placing || !agreed}
                className="w-full mt-4 h-12 rounded-full bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white disabled:opacity-50">
                {placing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...</> : <>Buat Pesanan & Bayar</>}
              </Button>
              <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> Pembayaran via QRIS, konfirmasi manual melalui WhatsApp.</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
